
import {onSSR} from '@vue-storefront/core';
import {useContent} from '@vue-storefront/storyblok';
import {computed, ssrRef, useRoute} from '@nuxtjs/composition-api';
import {useApplication} from '~/integrations/softgarden/src/composables/useApplication';
import {useJobs} from '~/integrations/softgarden/src/composables/useJobs';

import RenderContent from '~/components/RenderContent.vue';
import BaseInput from '~/components/BaseInput.vue';
import BaseTextarea from '~/components/BaseTextarea.vue';
import BaseDropdownMenu from '~/components/BaseDropdownMenu.vue';
import BaseInputCheckbox from '~/components/BaseInputCheckbox.vue';
import BaseInputFile from '~/components/BaseInputFile.vue';
import Modal from '~/components/Modal.vue';

export default {
  components: {
    RenderContent,
    BaseInput,
    BaseTextarea,
    BaseDropdownMenu,
    BaseInputFile,
    BaseInputCheckbox,
    Modal,
  },
  layout: 'default',
  setup() {
    const params = useRoute().value.params;
    const {
      search: sbSearch,
      content: sbContent,
    } = useContent('job-content');
    const {
      search: privacyPolicySearch,
      content: ppContent,
    } = useContent('privacy-policy-content');
    const story = ssrRef(() => sbContent.value);
    const privacyPolicyContent = computed(() => ppContent.value);

    const {
      loading: loadingCreateApplication,
      error: errorCreateApplication,
      response: responseCreateApplication,
      createApplicationByJobId,
    } = useApplication();

    const {
      searchJobById,
      jobs: job,
    } = useJobs();

    const jobId = params.jobId || null;

    onSSR(async () => {
      await searchJobById(jobId);
      const jobName = job.value['externalPostingName'] ?? '';

      await sbSearch({url: 'dynamic/applicationform', cache: false});
      story.value = JSON.parse(JSON.stringify(sbContent.value).replace('%job%', jobName));

      await privacyPolicySearch({url: 'rechtliches/datenschutzerklaerung', cache: false});

      if (typeof sbContent.value !== 'object') {
        return;
      }
    });

    return {
      jobId: params.jobId || null,
      job: job,
      content: story,
      loadingCreateApplication,
      errorCreateApplication,
      responseCreateApplication,
      createApplicationByJobId,
      privacyPolicyContent,
    };
  },
  data() {
    return {
      salutation: '',
      firstname: '',
      lastname: '',
      address: '',
      postcode: '',
      city: '',
      email: '',
      phone: '',
      coverLetter: '',
      externalProfileUrl: '',
      dataPrivacyAccepted: false,
      file: null,
      fileInputError: false,
      formError: false,
      formSuccess: false,
      howDoYouKnow: '',
      showClearFormModal: false,
      showPrivacyPolicyModal: false,
      errorFiles: [],
      customErrorMessage: 'Bitte probieren sie es nochmal',
    };
  },
  head() {
    return {
      title: 'Bewerbung - ' + this.job['externalPostingName'],
    };
  },
  methods: {
    async onSubmit(e) {
      e.preventDefault();
      let files = await this.filesToPostObject(this.file) || [];
      // check files type
      let data = {
        salutation: this.salutation,
        firstname: this.firstname,
        lastname: this.lastname,
        email: this.email,
        address: this.address,
        city: this.city,
        phone: this.phone,
        coverLetterText: this.coverLetter,
        externalProfileUrl: this.externalProfileUrl,
        dataPrivacyAccepted: this.dataPrivacyAccepted,
        jobId: this.jobId,
        files: files,
      };

      if (this.fileInputError) {
        return;
      }

      await this.createApplicationByJobId(data);

      if (this.responseCreateApplication.value.customMessage) {
        this.customErrorMessage = this.responseCreateApplication.value.customMessage;
        this.errorFiles = this.responseCreateApplication.value.files;
      }

      if (!this.responseCreateApplication.value.name || this.responseCreateApplication.value.name !== 'Error') {
        this.$gtm.push({ event: 'form_submit', formName: 'Bewerbung', jobId: data.jobId, jobName: this.jobName });
        this.formSuccess = true;
        this.resetForm();
      } else {
        this.formError = true;
      }
    },
    setSalutation(e) {
      this.salutation = e;
    },
    setHowDoYouKnow(e) {
      this.howDoYouKnow = e;
    },
    async filesToPostObject(fileList) {
      if (!fileList) {
        return [];
      }
      const promises = [];
      for (let i = 0; i < fileList.length; i++) {
        promises.push(this.fileToBase64(fileList[i]));
      }
      return Promise.all(promises)
        .then((resolve) => {
          return resolve;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    async fileToBase64(file) {
      let fileObject = {
        name: file.name || null,
        type: file.type || null,
        base64: null,
      };
      return await new Promise((resolve) => {
        let fileReader = new FileReader();
        fileReader.onload = (e) => {
          fileObject.base64 = fileReader.result || '';
          fileObject.base64 = encodeURIComponent(fileObject.base64);
          resolve(fileObject);
        };
        fileReader.readAsDataURL(file);
      });
    },
    closeErrorModal() {
      this.formError = false;
    },
    closeSuccessModal() {
      this.formSuccess = false;
    },
    clearForm() {
      this.salutation = '';
      this.firstname = '';
      this.lastname = '';
      this.address = '';
      this.postcode = '';
      this.city = '';
      this.email = '';
      this.phone = '';
      this.coverLetter = '';
      this.externalProfileUrl = '';
      this.dataPrivacyAccepted = false;
      this.file = null;
      this.fileInputError = false;
      this.formError = false;
      this.formSuccess = false;
      this.howDoYouKnow = '';
      this.showClearFormModal =false;
    },
    toggleClearFormModal() {
      this.showClearFormModal = !this.showClearFormModal;
    },
    handleInvalid(e) {
      e.target.classList.add('has-error');
    },
    openPrivacyPolicyModal() {
      this.showPrivacyPolicyModal = true;
    },
    closePrivacyPolicyModal() {
      this.showPrivacyPolicyModal = false;
    },
    attachFiles(file) {
      this.file = file;
      if (this.file.length && this.$refs.fileInput.toLargeFiles.length <= 0) {
        this.$refs.fileInput.showFileAttachedModal = true;
      }
    },
    deleteFile(file) {
      this.file = file;
    },
    resetForm() {
      this.salutation = '';
      this.firstname = '';
      this.lastname = '';
      this.address = '';
      this.postcode = '';
      this.city = '';
      this.email = '';
      this.phone = '';
      this.coverLetter = '';
      this.externalProfileUrl = '';
      this.dataPrivacyAccepted = true;
      this.file = null;
      this.fileInputError = false;
      this.howDoYouKnow = '';
      this.showClearFormModal = false;
      this.showPrivacyPolicyModal = false;
    },
  },
};
